import { Query, SortBy } from '@varo/graphql-schema-types';
import { REWARDS_HOME } from 'features/rewards/RewardsHome/RewardsHome.constants';
import getActivatedOffers from 'services/graphql/queries/rewards/GetActivatedOffers';
import getMyCashbackComponent from 'services/graphql/queries/rewards/GetMyCashbackComponent';
import getPerkSectionsV2 from 'services/graphql/queries/rewards/GetPerkSectionsV2';
import getRewardsTopCategories from 'services/graphql/queries/rewards/TopCategories';
import { useQuery } from 'urql';

interface UseRewardsQueriesProps {
  shouldPause: boolean;
  coordinates?: google.maps.LatLngLiteral;
  after: string;
}

export const useRewardsQueries = ({
  shouldPause,
  coordinates,
  after,
}: UseRewardsQueriesProps) => {
  const [
    {
      data: cashbackEarnedData,
      fetching: cashbackEarnedLoading,
      error: cashbackEarnedError,
    },
    refetchCashbackEarned,
  ] = useQuery<Query>({
    query: getMyCashbackComponent,
    pause: shouldPause,
    requestPolicy: 'cache-and-network',
  });

  const [
    {
      data: activatedOffersData,
      fetching: activatedOffersLoading,
      error: activatedOffersError,
    },
    refetchActivatedOffers,
  ] = useQuery<Query>({
    query: getActivatedOffers,
    pause: shouldPause,
    requestPolicy: 'cache-and-network',
  });

  const [
    {
      data: topCategoriesData,
      fetching: topCategoriesFetching,
      error: topCategoriesError,
    },
    refetchTopCategories,
  ] = useQuery<Query>({
    query: getRewardsTopCategories,
    variables: { sortTopCategoryBy: SortBy.OfferOrder },
    pause: shouldPause,
    requestPolicy: 'cache-first',
  });

  const coordinateInput = coordinates
    ? { latitude: coordinates.lat, longitude: coordinates.lng }
    : null;

  const [
    { data: sectionsData, fetching: sectionsFetching, error: rewardsError },
    refetchRewardsSections,
  ] = useQuery<Query>({
    query: getPerkSectionsV2,
    variables: {
      first: REWARDS_HOME.first,
      after: after,
      ...(coordinateInput && {
        input: { coordinate: coordinateInput },
        promoSectionInput: { coordinate: coordinateInput },
      }),
    },
    pause: shouldPause || !coordinateInput,
    requestPolicy: 'cache-and-network',
  });

  const isFetching =
    cashbackEarnedLoading ||
    activatedOffersLoading ||
    topCategoriesFetching ||
    sectionsFetching;
  const hasErrors = !!(
    cashbackEarnedError ||
    activatedOffersError ||
    topCategoriesError ||
    rewardsError
  );

  return {
    data: {
      cashbackEarnedData,
      activatedOffersData,
      topCategoriesData,
      sectionsData,
    },
    status: {
      isFetching,
      hasErrors,
      sectionsFetching,
    },
    refetch: {
      refetchCashbackEarned,
      refetchActivatedOffers,
      refetchTopCategories,
      refetchRewardsSections,
    },
  };
};
